import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { listAnalyses, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Printer, ShieldCheck, FilmStrip, Warning, CheckCircle, Pulse, EnvelopeSimple, X } from "@phosphor-icons/react";

const RISK_STYLES = {
  low: { dot: "bg-hazard-safe", text: "text-hazard-safe", label: "LOW" },
  medium: { dot: "bg-hazard-warning", text: "text-hazard-warning", label: "MEDIUM" },
  high: { dot: "bg-hazard-critical", text: "text-hazard-critical", label: "HIGH" },
  critical: { dot: "bg-hazard-critical", text: "text-hazard-critical", label: "CRITICAL" },
};

function todayISO() { return new Date().toISOString().slice(0, 10); }

export default function Report() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [startDate, setStartDate] = useState(params.get("start") || todayISO());
  const [endDate, setEndDate] = useState(params.get("end") || todayISO());
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEmail, setShowEmail] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { if (user?.email && !recipient) setRecipient(user.email); }, [user, recipient]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { setAll(await listAnalyses()); }
      catch (e) { toast.error("Could not load analyses"); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = new Date(`${startDate}T00:00:00`).getTime();
    const e = new Date(`${endDate}T23:59:59`).getTime();
    return all
      .filter((a) => {
        const t = new Date(a.created_at).getTime();
        return t >= s && t <= e && a.status === "complete" && a.result;
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [all, startDate, endDate]);

  const stats = useMemo(() => {
    if (filtered.length === 0) return null;
    const avg = Math.round(filtered.reduce((s, a) => s + (a.result.overall_risk_score || 0), 0) / filtered.length);
    const counts = filtered.reduce((acc, a) => {
      const k = a.result.risk_level || "medium";
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
    return { avg, counts, total: filtered.length };
  }, [filtered]);

  const updateRange = () => setParams({ start: startDate, end: endDate });

  const sendEmail = async () => {
    const to = (recipient || "").trim();
    if (!to || !to.includes("@")) { toast.error("Enter a valid email"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/report/email", { start: startDate, end: endDate, to });
      toast.success(`Report sent to ${data.to} (${data.count} clip${data.count === 1 ? "" : "s"})`);
      setShowEmail(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send report");
    } finally {
      setSending(false);
    }
  };


  return (
    <div className="min-h-screen bg-white">
      <Navbar variant="app" />
      <Toaster richColors position="top-right" />
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 report-controls">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-ink-900" data-testid="report-back">
            <ArrowLeft size={14} weight="bold" /> Back to dashboard
          </Link>
          <div className="flex items-center gap-2">
            <Button onClick={() => setShowEmail(true)} data-testid="report-email-cta" variant="outline" className="rounded-none border-ink-100 hover:border-ink-900">
              <EnvelopeSimple size={14} weight="bold" className="mr-2" /> Email me this report
            </Button>
            <Button onClick={() => window.print()} data-testid="report-print" className="rounded-none bg-safety hover:bg-safety-hover text-white">
              <Printer size={14} weight="bold" className="mr-2" /> Save as PDF
            </Button>
          </div>
        </div>

        <div className="mt-4 flex items-end justify-between flex-wrap gap-4 report-controls">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-1">Combined report</div>
            <h1 className="font-display font-black text-4xl tracking-tighter text-ink-900">Daily ergonomics report</h1>
            <div className="mt-2 text-ink-500 text-sm">{filtered.length} completed clip{filtered.length === 1 ? "" : "s"} between {startDate} and {endDate}</div>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">From</Label>
              <Input data-testid="report-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1 rounded-none border-ink-100 h-9" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">To</Label>
              <Input data-testid="report-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1 rounded-none border-ink-100 h-9" />
            </div>
            <Button variant="outline" onClick={updateRange} data-testid="report-apply" className="rounded-none border-ink-100 h-9">Apply</Button>
          </div>
        </div>

        {/* Print header — only visible in print */}
        <div className="hidden print:block mt-6 border-b border-ink-900 pb-4">
          <div className="flex items-center gap-2">
            <ShieldCheck weight="bold" size={18} className="text-safety" />
            <div className="font-display font-black text-2xl tracking-tighter text-ink-900">SafeForm.ai · Daily Ergonomics Report</div>
          </div>
          <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">
            Period {startDate} → {endDate} · Generated {new Date().toLocaleString()}
          </div>
        </div>

        {/* Roll-up */}
        {stats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-0 border border-ink-100">
            <StatCell label="Clips" value={stats.total} />
            <StatCell label="Avg risk" value={stats.avg} border />
            <StatCell label="High / critical" value={(stats.counts.high || 0) + (stats.counts.critical || 0)} tone="text-hazard-critical" border />
            <StatCell label="Low / medium" value={(stats.counts.low || 0) + (stats.counts.medium || 0)} tone="text-hazard-safe" border />
          </div>
        )}

        {/* Per-clip section */}
        {loading ? (
          <div className="mt-8 text-center font-mono text-xs uppercase tracking-[0.3em] text-ink-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="mt-8 border border-ink-100 p-12 text-center text-ink-500">
            No completed analyses in that window.
          </div>
        ) : (
          <div className="mt-8 space-y-6">
            {filtered.map((a, idx) => {
              const r = a.result || {};
              const rs = RISK_STYLES[r.risk_level] || RISK_STYLES.medium;
              const hazards = Array.isArray(r.hazards) ? r.hazards : [];
              return (
                <div key={a.id} className="border border-ink-100 break-inside-avoid" data-testid={`report-item-${a.id}`}>
                  <div className="grid grid-cols-3 bg-ink-50 p-3 border-b border-ink-100 font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">
                    <span>{String(idx + 1).padStart(2, "0")} · {a.id.slice(0, 8).toUpperCase()}</span>
                    <span className="text-center">{new Date(a.created_at).toLocaleDateString()}</span>
                    <span className="text-right">{a.industry} · {a.task_type}</span>
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-bold text-xl tracking-tight text-ink-900">{a.title}</div>
                        <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500">{a.duration_minutes}min/shift</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`w-2 h-2 ${rs.dot}`} />
                        <span className={`font-mono text-[10px] uppercase tracking-[0.25em] ${rs.text}`}>{rs.label}</span>
                        <span className="font-display font-bold text-3xl tracking-tighter text-ink-900">{r.overall_risk_score}<span className="text-sm font-mono text-ink-500">/100</span></span>
                      </div>
                    </div>
                    <p className="mt-3 text-ink-900 leading-relaxed">{r.summary}</p>

                    {hazards.length > 0 && (
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        {hazards.slice(0, 4).map((h, i) => {
                          const color = h.severity === "low" ? "bg-hazard-safe" : (h.severity === "medium" ? "bg-hazard-warning" : "bg-hazard-critical");
                          return (
                            <div key={i} className="flex items-start gap-2 border-l-2 border-ink-100 pl-3">
                              <span className={`mt-1 w-1.5 h-1.5 ${color} shrink-0`} />
                              <div className="min-w-0">
                                <div className="font-semibold text-ink-900">{h.name}</div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-500">{h.body_region} · {(h.severity || "").toUpperCase()}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="mt-4 border-l-2 border-safety pl-3">
                      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Safer technique</div>
                      <p className="mt-1 text-ink-900 text-sm leading-relaxed">{r.safer_technique}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Email-this-report modal */}
      {showEmail && (
        <div className="fixed inset-0 z-50 bg-ink-900/60 flex items-center justify-center p-4 report-controls" onClick={() => !sending && setShowEmail(false)}>
          <div className="bg-white border-2 border-ink-900 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="report-email-modal">
            <div className="flex items-center justify-between p-5 border-b border-ink-100">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Email roll-up</div>
              <button onClick={() => setShowEmail(false)} className="text-ink-500 hover:text-ink-900" aria-label="Close"><X size={16} weight="bold" /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-ink-700">Sends the {startDate} → {endDate} roll-up as a designed HTML email — including risk scores, top hazards per clip, and deep-links to each analysis.</p>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Send to</Label>
                <Input
                  data-testid="report-email-input"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder="supervisor@safeform.ai"
                  type="email"
                  className="mt-2 rounded-none border-ink-100 focus-visible:ring-safety"
                />
                <div className="mt-2 text-[10px] font-mono uppercase tracking-[0.25em] text-ink-500">
                  Defaults to your account email · use any address you can verify.
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-ink-100 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEmail(false)} className="rounded-none border-ink-100" disabled={sending}>Cancel</Button>
              <Button data-testid="report-email-send" onClick={sendEmail} disabled={sending} className="rounded-none bg-safety hover:bg-safety-hover text-white">
                {sending ? "Sending…" : "Send report"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCell({ label, value, border, tone }) {
  return (
    <div className={`p-5 ${border ? "md:border-l border-ink-100" : ""}`}>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">{label}</div>
      <div className={`mt-2 font-display font-black text-3xl tracking-tighter text-ink-900 ${tone || ""}`}>{value}</div>
    </div>
  );
}
